import * as React from 'react'
import { useMemo } from 'react'
import {
    Accordion,
    AccordionHeader,
    AccordionItem,
    AccordionPanel,
    Button,
    Field,
    InteractionTag,
    InteractionTagPrimary,
    makeStyles,
    tokens,
    Tooltip,
} from '@fluentui/react-components'
import { AddFilled, AddRegular, bundleIcon, CalendarDayRegular, CalendarMonthRegular, CalendarWorkWeekRegular } from '@fluentui/react-icons'
import useModules from '../../../hooks/module/useModules'
import { useTranslation } from 'react-i18next'
import useViewport, { Viewport } from '../../../hooks/useViewport'
import UserPicker from '../../../components/TagPicker/UserPicker'
import { ModuleType } from '../../../context/GlobalContext'
import AttributesList from '../../../components/Form/Attributes/AttributesList'
import { AttributeContextType, AttributeEntityDto, AttributeViewType, EntityAttributeType } from '../../../hooks/useAttributes'
import { useFormikContext } from 'formik'
import useAxios from '../../../hooks/useAxios'
import { SortDirection } from '../../../config/settingConfig'
import { AxiosResponse } from 'axios'
import { BaseOverviewCount, BaseOverviewLineItemProps, parseFilterValuesToParams, useOverviewStyles } from '../../../pages/BaseOverview/BaseOverview'
import { BaseOverviewFilterProps, BaseOverviewFilterType, generateFromDate, generateToDate } from '../../../pages/BaseOverview/BaseOverviewFilter'
import DateRange from '../../../components/DatePicker/DateRange'
import BooleanCombobox from '../../../components/Combobox/BooleanCombobox'
import FilePicker from '../../../components/TagPicker/FilePicker'
import { CheckboxOnChangeData } from '@fluentui/react-checkbox'
import { DateTime } from 'luxon'
import { FixedPriceMutationDto, FixedPriceOverviewDto } from '../../../config/registrationConfig'
import { MoneyDto } from '../../../config/i18nConfig'
import { BaseOverviewSortingType } from '../../../pages/BaseOverview/BaseSortMenu'
import BaseMutableOverview, {
    BaseMutableOverviewActionButtonProps,
    BaseMutableOverviewProps,
} from '../../../pages/BaseMutableOverview/BaseMutableOverview'
import FixedPriceHeaderItem from './FixedPriceHeaderItem'
import FixedPriceListItem from './FixedPriceListItem'
import { formatIsoDate } from '../../../helpers/dateHelper'
import { mapFixedPriceOverviewDtoToMutation } from '../../../config/registration/fixedPriceConfig'
import FixedPriceMutationDialogBody from '../../components/Dialog/Registration/FixedPriceMutationDialogBody'
import useUser from '../../../hooks/useUser'
import { IFileDto } from '../../../config/fileConfig'

const AddIcon = bundleIcon(AddFilled, AddRegular)

export const useItemStyles = makeStyles({
    listFixedPriceItem: {
        gridTemplateColumns:
            'var(--baseOverviewAvatarWidth) var(--baseOverviewDateWidth) auto var(--baseOverviewPriceWidth) var(--baseOverviewPriceWidth)',
    },
    listFixedPriceItemSelectable: {
        gridTemplateColumns:
            'var(--baseOverviewCheckboxWidth) var(--baseOverviewAvatarWidth) var(--baseOverviewDateWidth) auto var(--baseOverviewPriceWidth) min-content',
    },
    listFixedPriceItemSmall: {
        gridTemplateColumns: 'var(--baseOverviewDateWidth) auto',
        gridTemplateRows: 'auto auto',
    },
    listFixedPriceItemSmallSelectable: {
        gridTemplateColumns: 'var(--baseOverviewCheckboxWidth) var(--baseOverviewDateWidth) auto',
        gridTemplateRows: 'auto auto',
    },
    listFixedPriceItemSmallBottomRow: {
        display: 'grid',
        gridTemplateColumns: 'auto var(--baseOverviewPriceWidth) var(--baseOverviewPriceWidth)',
        gap: tokens.spacingVerticalS,
    },
    listFixedPriceItemTinyBottomRow: {
        display: 'grid',
        gridTemplateColumns: 'auto var(--baseOverviewPriceWidth)',
        gap: tokens.spacingVerticalS,
    },
    listFixedPriceItemOverflow: {
        overflowWrap: 'anywhere',
    },
    turnoverField: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: tokens.spacingVerticalS,
    },
})

type BaseItem = FixedPriceOverviewDto

export type OverviewItem = {
    // Is calculated during runtime and not returned by the API. Expressed in American number notation (0.00).
    turnover: MoneyDto
    invoiceTurnover: MoneyDto
} & BaseItem

export type FixedPriceOverviewProps = BaseMutableOverviewProps<
    ExtendedFixedPriceOverviewDto,
    FixedPriceFilterType,
    FixedPriceSortingType,
    FixedPriceOverviewCount
>

export type BaseFixedPriceOverviewProps = Partial<FixedPriceOverviewProps> & {
    baseUrl: string

    file?: IFileDto
    fileId?: string
    hideFileField?: boolean
    hideInvoiceField?: boolean
    renderLink?: boolean
}

export type BaseFixedPriceLineOverviewProps = BaseFixedPriceOverviewProps &
    BaseOverviewLineItemProps<ExtendedFixedPriceOverviewDto, FixedPriceFilterType, FixedPriceSortingType>

export type FixedPriceOverviewCount = BaseOverviewCount & {
    currencies: FixedPriceCurrencyOverviewCount[]
}

export type FixedPriceCurrencyOverviewCount = {
    currency: string
    distance: number
    turnover: string
    invoiceTurnover: string
}

export type ExtendedFixedPriceOverviewDto = FixedPriceOverviewDto & {
    // Is calculated during runtime and not actually returned by the API. Expressed in American number notation (0.00).
    turnover: MoneyDto
    invoiceTurnover: MoneyDto
}

export type FixedPriceFilterType = AttributeEntityDto &
    BaseOverviewFilterType & {
        from?: string
        to?: string
        users?: string[]
        isBilled?: boolean
        files?: string[]
        fileOwners?: string[]
        isInvoiceApproval?: boolean
        invoiceApprovals?: string[]
    }

export type FixedPriceSorting = {
    id: string
    date: string
    description: string
    'file.name': string
    'file.reference': string
}

export type FixedPriceSortingType = BaseOverviewSortingType<FixedPriceSorting>

type BaseTaskOverviewFilterProps = BaseFixedPriceOverviewProps &
    BaseOverviewFilterProps<ExtendedFixedPriceOverviewDto, FixedPriceFilterType, FixedPriceSortingType>

function FixedPriceFilter(props: BaseTaskOverviewFilterProps) {
    const { values, setFieldValue } = useFormikContext<FixedPriceFilterType>()
    const modules = useModules()
    const { t: translate } = useTranslation()

    const orientation = useMemo(() => (props.viewportMedium ? 'vertical' : 'horizontal'), [props.viewportMedium])

    const updateFieldValue = (field: string, value: any, shouldValidate?: boolean) => {
        setFieldValue('tag', undefined, false).then(() => setFieldValue(field, value, shouldValidate))
    }

    return (
        <>
            <DateRange
                orientation={orientation}
                startValue={values.from}
                endValue={values.to}
                changeStartDate={date => updateFieldValue('from', date)}
                changeEndDate={date => updateFieldValue('to', date)}
                showLabel
            />
            <Field label={translate('registration.filter.user')} orientation={orientation}>
                <UserPicker
                    selectedOptions={values.users || []}
                    onOptionsSelect={(_, selectedOptions) => updateFieldValue('users', selectedOptions)}
                />
            </Field>
            {!values.isInvoiceApproval && modules.has(ModuleType.Invoices) && (
                <Field label={translate('registration.filter.isBilled')} orientation={orientation}>
                    <BooleanCombobox
                        selectedOptions={[values.isBilled?.toString() || 'mixed']}
                        onOptionSelect={(_, data: CheckboxOnChangeData) =>
                            updateFieldValue('isBilled', data.checked === 'mixed' ? undefined : data.checked)
                        }
                    />
                </Field>
            )}
            {!props.hideFileField && (
                <Field label={translate('registration.filter.file')} orientation={orientation}>
                    <FilePicker
                        selectedOptions={values.files || []}
                        onOptionsSelect={(_, selectedOptions) => updateFieldValue('files', selectedOptions)}
                    />
                </Field>
            )}
            {modules.has(ModuleType.Attributes) && (
                <Accordion collapsible>
                    <AccordionItem value="attributes">
                        <AccordionHeader>{translate('registration.filter.attributes')}</AccordionHeader>
                        <AccordionPanel>
                            <AttributesList
                                orientation={orientation}
                                entityType={EntityAttributeType.File}
                                context={[AttributeContextType.File]}
                                viewType={AttributeViewType.Filter}
                                entity={values}
                                setFieldValue={(field, value) => updateFieldValue(values[field], value)}
                            />
                        </AccordionPanel>
                    </AccordionItem>
                </Accordion>
            )}
        </>
    )
}
const filterTagItems: FixedPriceFilterType[] = [
    {
        from: DateTime.local().toISODate(),
        to: DateTime.local().toISODate(),
        tag: 'day',
        icon: <CalendarDayRegular />,
        title: 'fixedprice.filter.today',
    },
    {
        from: DateTime.local().startOf('week').toISODate(),
        to: DateTime.local().endOf('week').toISODate(),
        tag: 'week',
        icon: <CalendarWorkWeekRegular />,
        title: 'fixedprice.filter.week',
    },
    {
        from: DateTime.local().startOf('month').toISODate(),
        to: DateTime.local().endOf('month').toISODate(),
        tag: 'month',
        icon: <CalendarMonthRegular />,
        title: 'fixedprice.filter.month',
    },
]

const headerSortingItems: FixedPriceSortingType[] = [
    { property: ['date', 'id'], direction: SortDirection.Ascending },
    { property: ['date', 'id'], direction: SortDirection.Descending, default: true },
    { property: ['description', 'id'], direction: SortDirection.Ascending, descriptionKey: 'sorting.descriptionaz' },
    { property: ['description', 'id'], direction: SortDirection.Descending, descriptionKey: 'sorting.descriptionza' },
    { property: ['file.name', 'id'], direction: SortDirection.Ascending, descriptionKey: 'sorting.fileaz' },
    { property: ['file.name', 'id'], direction: SortDirection.Descending, descriptionKey: 'sorting.fileza' },
    { property: ['file.reference', 'id'], direction: SortDirection.Ascending, descriptionKey: 'sorting.referenceasc' },
    {
        property: ['file.reference', 'id'],
        direction: SortDirection.Descending,
        descriptionKey: 'sorting.referencedesc',
    },
]

function HeaderFilterTags(props: FixedPriceOverviewProps) {
    const { values, setValues } = useFormikContext<FixedPriceFilterType>()
    const overviewStyles = useOverviewStyles()
    const { t: translate } = useTranslation()
    const { viewport, isViewport } = useViewport()

    const viewportNarrow = useMemo<boolean>(() => isViewport('<=', Viewport.OutlookNew), [viewport])

    return (
        <div className={overviewStyles.flexGapXS}>
            {filterTagItems.map((tag, index) => (
                <InteractionTag
                    key={index}
                    selected={values.tag === tag.tag}
                    onClick={() =>
                        setValues({
                            ...props.defaultFilterValues,
                            ...tag,
                        })
                    }
                    size={viewportNarrow ? 'small' : 'medium'}
                >
                    {viewportNarrow && (
                        <Tooltip withArrow relationship={'description'} content={translate(tag.title)}>
                            <InteractionTagPrimary icon={tag.icon} />
                        </Tooltip>
                    )}
                    {!viewportNarrow && <InteractionTagPrimary icon={tag.icon}>{translate(tag.title)}</InteractionTagPrimary>}
                </InteractionTag>
            ))}
        </div>
    )
}

function HeaderActionsButtons(
    props: BaseOverviewFilterProps<
        ExtendedFixedPriceOverviewDto,
        FixedPriceFilterType,
        FixedPriceSortingType,
        FixedPriceOverviewCount,
        BaseMutableOverviewActionButtonProps<any>
    >,
) {
    const { t: translate } = useTranslation()

    return (
        <Tooltip withArrow relationship={'label'} content={translate('action.create.fixedprice.title')}>
            <Button icon={<AddIcon />} appearance={'primary'} onClick={() => props.setOpenMutationDialog(true, null)} />
        </Tooltip>
    )
}

/**
 * The mutable fixed-price overview: a BaseMutableOverview with the filter, the add/edit popup and a
 * create button wired in — like BaseTimeOverview. The dossier wrapper (FixedPriceOverview) scopes to
 * the file; the declaration advice passes selection handlers and its own header but keeps the create
 * button.
 */
export default function BaseFixedPriceOverview(props: BaseFixedPriceOverviewProps) {
    const axios = useAxios()
    const itemStyles = useItemStyles()
    const { currentUser } = useUser()

    const defaultFilterValues = useMemo<FixedPriceFilterType>(
        () => ({
            from: '',
            to: '',
            users: [],
            attributes: [],
            query: '',
            tag: undefined,
            ...props.fixedFilterValues,
            ...props.defaultFilterValues,
        }),
        [props.fixedFilterValues, props.defaultFilterValues],
    )

    const countItems = (_column?: number, filterValues?: FixedPriceFilterType): Promise<FixedPriceOverviewCount> => {
        return axios.get('/fixed-price/count', { params: parseFilterValuesToParams(filterValues) }).then(response => response.data)
    }

    const loadItems = (
        _column?: number,
        filterValues?: FixedPriceFilterType,
        sortingValues?: FixedPriceSortingType,
        start?: number,
        size?: number,
    ): Promise<ExtendedFixedPriceOverviewDto[]> => {
        return axios
            .get('/fixed-price', {
                params: { ...parseFilterValuesToParams(filterValues, sortingValues, start, size) },
            })
            .then((response: AxiosResponse<OverviewItem[]>): ExtendedFixedPriceOverviewDto[] =>
                (response?.data || []).map((item: OverviewItem) => ({
                    ...item,
                    turnover: item.rate,
                    invoiceTurnover: item.price && {
                        amount: item.price.subtotal,
                        currency: item.price.currency,
                    },
                })),
            )
    }

    return (
        <BaseMutableOverview<
            ExtendedFixedPriceOverviewDto,
            FixedPriceFilterType,
            FixedPriceSortingType,
            FixedPriceOverviewCount,
            BaseMutableOverviewActionButtonProps<ExtendedFixedPriceOverviewDto>,
            any,
            FixedPriceMutationDto
        >
            defaultFilterValues={defaultFilterValues}
            countItems={countItems}
            loadItems={loadItems}
            listItemHeaderContent={FixedPriceHeaderItem}
            listItemContent={(props: BaseFixedPriceLineOverviewProps) => <FixedPriceListItem {...props} />}
            listItemClassnames={props => [
                props.onItemSelected ? itemStyles.listFixedPriceItemSelectable : itemStyles.listFixedPriceItem,
                props.viewportNarrow && (props.onItemSelected ? itemStyles.listFixedPriceItemSmallSelectable : itemStyles.listFixedPriceItemSmall),
            ]}
            headerDefaultFilterTags={HeaderFilterTags}
            headerSortingItems={headerSortingItems}
            headerActionsButtons={HeaderActionsButtons}
            filterContent={FixedPriceFilter}
            filterFromJson={values => ({
                ...values,
                from: values.tag ? generateFromDate(values.tag) : values.from,
                to: values.tag ? generateToDate(values.tag) : values.to,
            })}
            filterToJson={values => ({
                ...values,
                from: values.tag ? '' : values.from,
                to: values.tag ? '' : values.to,
                icon: undefined,
                title: undefined,
            })}
            inputPlaceholderKey={'fixedprice.filter.search'}
            mutationDialogFormProps={item => ({
                initialValues: item
                    ? mapFixedPriceOverviewDtoToMutation(item)
                    : {
                          fileId: props.fileId,
                          userId: currentUser?.id,
                          date: formatIsoDate(new Date()),
                          type: undefined,
                          name: undefined,
                          description: undefined,
                          longDescription: undefined,
                          currency: props.file?.currency,
                          vatType: undefined,
                          rate: undefined,
                      },
                onSubmit: () => {},
            })}
            mutationDialogBodyProps={item => ({
                item,
            })}
            mutationDialogBody={FixedPriceMutationDialogBody}
            mutationDialogProps={{ warnOnUnsavedChanges: true }}
            {...props}
        />
    )
}
